import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import './tooltip.css';

const MARGIN = 8;
const OFFSET = 5;

const Tooltip = ({
  align,
  text,
  children,
  width,
  height,
  id,
  disable,
  onClick,
}) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState(null);

  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);

  const showTooltip = () => {
    if (!disable) {
      setVisible(true);
    }
  };

  const hideTooltip = () => {
    setVisible(false);
    setPosition(null);
  };

  useLayoutEffect(() => {
    if (!visible || !wrapperRef.current || !tooltipRef.current) {
      return;
    }

    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    const tooltipWidth = tooltipRect.width;
    const tooltipHeight = tooltipRect.height;

    // Position horizontale préférée.
    let side = align ?? 'right';

    // Vérifie l'espace disponible de chaque côté.
    const spaceRight = window.innerWidth - wrapperRect.right - OFFSET;
    const spaceLeft = wrapperRect.left - OFFSET;

    if (side === 'right' && spaceRight < tooltipWidth && spaceLeft > spaceRight) {
      side = 'left';
    } else if (side === 'left' && spaceLeft < tooltipWidth && spaceRight > spaceLeft) {
      side = 'right';
    }

    // Calcul de la position horizontale.
    let left;

    if (side === 'left') {
      left = wrapperRect.left - tooltipWidth - OFFSET;
    } else {
      left = wrapperRect.right + OFFSET;
    }

    // Empêche le tooltip de dépasser horizontalement de la fenêtre.
    left = Math.max(
      MARGIN,
      Math.min(left, window.innerWidth - tooltipWidth - MARGIN)
    );

    // Centre verticalement le tooltip par rapport au composant.
    let top = wrapperRect.top + (wrapperRect.height - tooltipHeight) / 2;

    // Empêche le tooltip de dépasser verticalement de la fenêtre.
    top = Math.max(
      MARGIN,
      Math.min(top, window.innerHeight - tooltipHeight - MARGIN)
    );

    top = top + 20;
    setPosition({ left, top });
  }, [visible, align, text]);

  useEffect(() => {
    if (!visible) return;

    const updatePosition = () => {
      const wrapper = wrapperRef.current;
      const tooltip = tooltipRef.current;

      if (!wrapper || !tooltip) return;

      const wrapperRect = wrapper.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();

      let side = align ?? 'right';

      const spaceRight = window.innerWidth - wrapperRect.right - OFFSET;
      const spaceLeft = wrapperRect.left - OFFSET;

      if (side === 'right' && spaceRight < tooltipRect.width && spaceLeft > spaceRight) {
        side = 'left';
      } else if (side === 'left' && spaceLeft < tooltipRect.width && spaceRight > spaceLeft) {
        side = 'right';
      }

      let left = side === 'left'
        ? wrapperRect.left - tooltipRect.width - OFFSET
        : wrapperRect.right + OFFSET;

      left = Math.max(
        MARGIN,
        Math.min(left, window.innerWidth - tooltipRect.width - MARGIN)
      );

      let top = wrapperRect.top + (wrapperRect.height - tooltipRect.height) / 2 ;

      top = Math.max(
        MARGIN,
        Math.min(top, window.innerHeight - tooltipRect.height - MARGIN)
      );

      top = top + 20;

      setPosition({ left, top  });
    };

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [visible, align]);

  function onKeydown(event) {
    if (event.key === 'Enter' && !disable) {
      event.preventDefault();
      event.stopPropagation();

      if (onClick) onClick();
    }
  }

  return (
    <div
      ref={wrapperRef}
      id={id}
      className="tooltip-wrapper"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      role="button"
      tabIndex={0}
      style={{
        width: width != null ? width + 'px' : undefined,
        height: height != null ? height + 'px' : undefined,
      }}
      onKeyDown={onKeydown}
      aria-label={text}
    >
      {children}

      {visible && createPortal(
        <div
          ref={tooltipRef}
          className="tooltip-portal"
          style={{
            position: 'fixed',
            left: position ? `${position.left}px` : '-10000px',
            top: position ? `${position.top}px` : '-10000px',
            visibility: position ? 'visible' : 'hidden',
          }}
        >
          {text}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Tooltip;